import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { useList, errMsg, fmtRM, dayName, mediaUrl } from "@/lib/api";
import { Spinner, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PublicLayout } from "@/pages/public/PublicSite";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, CheckCircle2, Clock, Music4, CalendarDays, User, ClipboardList, CalendarSearch,
} from "lucide-react";

const STEPS = ["Course", "Date & time", "Your details", "Review"];
const todayStr = () => new Date().toISOString().slice(0, 10);
const initials = (n) => (n || "").split(" ").map((w) => w[0]).slice(0, 2).join("");

const DAYS_OF_WEEK = [
  { v: 0, label: "Monday" }, { v: 1, label: "Tuesday" }, { v: 2, label: "Wednesday" },
  { v: 3, label: "Thursday" }, { v: 4, label: "Friday" }, { v: 5, label: "Saturday" }, { v: 6, label: "Sunday" },
];
// Common lesson-hour time options (08:00 → 21:30)
const TIME_OPTIONS = (() => {
  const out = [];
  for (let h = 8; h <= 21; h++) {
    for (const m of [0, 30]) {
      out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return out;
})();

function PreferredTimeDialog({ open, onOpenChange, course }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    parent_name: "", parent_phone: "", parent_email: "",
    student_name: "", student_age: "",
    notes: "",
    choices: [
      { day_of_week: "", time_from: "", time_to: "" },
      { day_of_week: "", time_from: "", time_to: "" },
      { day_of_week: "", time_from: "", time_to: "" },
    ],
  });
  const setChoice = (i, k, v) => setForm((f) => {
    const c = [...f.choices]; c[i] = { ...c[i], [k]: v }; return { ...f, choices: c };
  });
  const close = (o) => {
    if (!o) setForm({ ...form, parent_name: "", parent_phone: "", parent_email: "",
      student_name: "", student_age: "", notes: "",
      choices: [{ day_of_week: "", time_from: "", time_to: "" },
                { day_of_week: "", time_from: "", time_to: "" },
                { day_of_week: "", time_from: "", time_to: "" }] });
    onOpenChange(o);
  };
  const submit = async () => {
    if (!course?.id) { toast.error("Pick a course first"); return; }
    if (!form.parent_name.trim() || !form.parent_phone.trim() || !form.student_name.trim()) {
      toast.error("Parent name, phone and student name are required"); return;
    }
    const choices = form.choices
      .filter((c) => c.day_of_week !== "" && c.time_from && c.time_to)
      .map((c) => ({ day_of_week: Number(c.day_of_week), time_from: c.time_from, time_to: c.time_to }));
    if (choices.length === 0) {
      toast.error("Add at least one preferred day + time range"); return;
    }
    setBusy(true);
    try {
      await api.post("/public/preferred-time-request", {
        parent_name: form.parent_name, parent_phone: form.parent_phone,
        parent_email: form.parent_email || null,
        student_name: form.student_name, student_age: form.student_age || null,
        course_id: course.id, notes: form.notes || null,
        preferred_time_choices: choices,
      });
      toast.success("Thanks! Our team will contact you within 1 working day.");
      close(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl" data-testid="preferred-time-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarSearch className="h-5 w-5 text-secondary" />
            Tell us your preferred times
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <p className="text-xs text-muted-foreground">
            Couldn't find a slot that works? Drop us 1st, 2nd and 3rd preferred day &amp; time ranges for
            {course?.name ? <> <b className="text-foreground">{course.name}</b></> : <> your chosen course</>} —
            our admin team will reach out with the closest available match.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Parent name *</Label>
              <Input value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })}
                data-testid="pt-parent-name" /></div>
            <div><Label className="text-xs">Phone *</Label>
              <Input value={form.parent_phone} onChange={(e) => setForm({ ...form, parent_phone: e.target.value })}
                data-testid="pt-parent-phone" placeholder="012-345 6789" /></div>
            <div className="sm:col-span-2"><Label className="text-xs">Email (optional)</Label>
              <Input value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })}
                data-testid="pt-parent-email" type="email" /></div>
            <div><Label className="text-xs">Student name *</Label>
              <Input value={form.student_name} onChange={(e) => setForm({ ...form, student_name: e.target.value })}
                data-testid="pt-student-name" /></div>
            <div><Label className="text-xs">Student age</Label>
              <Input value={form.student_age} onChange={(e) => setForm({ ...form, student_age: e.target.value })}
                data-testid="pt-student-age" placeholder="e.g. 8" /></div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Preferred slots (1st / 2nd / 3rd)</Label>
            {form.choices.map((c, i) => (
              <div key={i} className="rounded-md border bg-card p-3 grid grid-cols-1 sm:grid-cols-[24px_1fr_1fr_1fr] gap-2 items-end"
                   data-testid={`pt-choice-${i}`}>
                <p className="text-base font-bold text-secondary pb-2">{i + 1}.</p>
                <div>
                  <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Day</Label>
                  <Select value={String(c.day_of_week)} onValueChange={(v) => setChoice(i, "day_of_week", v)}>
                    <SelectTrigger data-testid={`pt-day-${i}`}><SelectValue placeholder="Day" /></SelectTrigger>
                    <SelectContent>
                      {DAYS_OF_WEEK.map((d) => <SelectItem key={d.v} value={String(d.v)}>{d.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">From</Label>
                  <Select value={c.time_from} onValueChange={(v) => setChoice(i, "time_from", v)}>
                    <SelectTrigger data-testid={`pt-from-${i}`}><SelectValue placeholder="From" /></SelectTrigger>
                    <SelectContent className="max-h-[260px]">
                      {TIME_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">To</Label>
                  <Select value={c.time_to} onValueChange={(v) => setChoice(i, "time_to", v)}>
                    <SelectTrigger data-testid={`pt-to-${i}`}><SelectValue placeholder="To" /></SelectTrigger>
                    <SelectContent className="max-h-[260px]">
                      {TIME_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
          <div>
            <Label className="text-xs">Anything else? (optional)</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="pt-notes" placeholder="e.g. flexible on Saturdays, beginner level, etc." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="pt-submit" className="bg-secondary hover:bg-[#D96C52]">
            {busy ? "Submitting…" : "Submit preferred times"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StepBar({ step }) {
  return (
    <div className="flex items-center gap-2 mb-10 flex-wrap" data-testid="wizard-steps">
      {STEPS.map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <span className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold
            ${i < step ? "bg-[#2D6A4F] text-white" : i === step ? "bg-secondary text-white" : "bg-muted text-muted-foreground"}`}>
            {i < step ? "✓" : i + 1}
          </span>
          <span className={`text-xs font-medium hidden sm:block ${i === step ? "text-foreground" : "text-muted-foreground"}`}>{s}</span>
          {i < STEPS.length - 1 && <span className="w-5 h-px bg-border hidden sm:block" />}
        </div>
      ))}
    </div>
  );
}

function HoldCountdown({ expiresAt, onExpired }) {
  const [left, setLeft] = useState(null);
  useEffect(() => {
    const tick = () => {
      const ms = new Date(expiresAt) - new Date();
      if (ms <= 0) { setLeft(0); onExpired(); return; }
      setLeft(ms);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]); // eslint-disable-line
  if (left == null || left <= 0) return null;
  const m = Math.floor(left / 60000), s = Math.floor((left % 60000) / 1000);
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-bold text-secondary" data-testid="hold-countdown">
      <Clock className="h-3.5 w-3.5" />Slot held for {m}:{String(s).padStart(2, "0")}
    </span>
  );
}

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const preselectCourseId = searchParams.get("courseId");
  const preselectTeacherId = searchParams.get("teacherId");
  const preselectMaster = searchParams.get("master");  // simplified name filter
  const [step, setStep] = useState(0);
  const [session, setSession] = useState(null);
  // When a Music Instructor is pre-selected via URL, narrow the course catalogue
  // to only courses that instructor is tagged to teach.
  const coursesEndpoint = preselectTeacherId
    ? `/public/courses?teacher_id=${preselectTeacherId}`
    : "/public/courses";
  const { data: courses } = useList(coursesEndpoint);
  const [courseTeachers, setCourseTeachers] = useState(null);
  const [course, setCourse] = useState(null);
  const [teacher, setTeacher] = useState(null);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [hold, setHold] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [holidayInfo, setHolidayInfo] = useState(null);
  const [rulesAccepted, setRulesAccepted] = useState(false);
  const [rules, setRules] = useState(null);
  const [paymentReturn, setPaymentReturn] = useState(null);  // { status, ...details }
  const [quote, setQuote] = useState(null);
  const holdRef = useRef(null);
  holdRef.current = hold;
  const [didPreselect, setDidPreselect] = useState(false);
  const [prefDialogOpen, setPrefDialogOpen] = useState(false);  // Phase 3a — preferred-time submission

  // Fetch the all-in payable amount once parent reaches the Review step
  useEffect(() => {
    if (step === 3 && course?.id) {
      api.get(`/public/enrolments/quote?course_id=${course.id}`)
        .then((r) => setQuote(r.data))
        .catch(() => setQuote(null));
    }
  }, [step, course]);

  // Detect Stripe return (?session_id=...) and poll until payment is reconciled
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (params.get("cancelled")) {
      toast.error("Payment cancelled — your slot has been released.");
      window.history.replaceState({}, "", "/register");
      return;
    }
    if (!sid) return;
    setPaymentReturn({ status: "polling" });
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const r = await api.get(`/public/enrolments/status/${sid}`);
        if (r.data.payment_status === "paid") {
          setPaymentReturn({ status: "paid", ...r.data });
          return;
        }
        if (r.data.status === "expired") {
          setPaymentReturn({ status: "expired" });
          return;
        }
      } catch (e) { /* keep retrying briefly */ }
      if (tries < 12) setTimeout(poll, 2500);
      else setPaymentReturn({ status: "timeout" });
    };
    poll();
  }, []);

  useEffect(() => {
    api.post("/public/registration/session", { source_channel: "website" })
      .then((r) => setSession(r.data))
      .catch(() => toast.error("Could not start registration — please refresh."));
    api.get("/public/school-rules").then((r) => setRules(r.data)).catch(() => {});
    return () => { // release any live hold on unmount
      if (holdRef.current) api.post(`/public/booking-holds/${holdRef.current.id}/release`).catch(() => {});
    };
  }, []);

  const releaseHold = async () => {
    if (hold) {
      api.post(`/public/booking-holds/${hold.id}/release`).catch(() => {});
      setHold(null);
    }
  };

  const loadSlots = async (d, t = teacher) => {
    setLoadingSlots(true);
    setSlots(null);
    setHolidayInfo(null);
    try {
      // Course-based aggregation (any eligible teacher). If a specific teacher
      // was deep-linked, fall back to the per-teacher endpoint.
      const r = t
        ? await api.get(`/public/booking/slots?teacher_id=${t.id}&date=${d}`)
        : await api.get(`/public/booking/slots-by-course?course_id=${course.id}&date=${d}`);
      setSlots(r.data.slots);
      if (r.data.is_holiday) setHolidayInfo({ label: r.data.holiday_label, type: r.data.holiday_type });
    } catch (e) { toast.error(errMsg(e)); } finally { setLoadingSlots(false); }
  };

  const pickSlot = async (slot) => {
    if (!session) { toast.error("Still initialising — one moment please"); return; }
    await releaseHold();
    // For course-based slots, the slot carries the teacher_id (first available
    // teacher for that time). Auto-pick that teacher so the rest of the flow
    // (booking-hold, enrolment) keeps working unchanged.
    const slotTeacherId = teacher?.id || slot.teacher_id;
    if (!teacher && slot.teacher_id) {
      setTeacher({ id: slot.teacher_id, full_name: slot.teacher_name });
    }
    try {
      const r = await api.post("/public/booking-holds", {
        session_id: session.session_id, course_id: course.id, teacher_id: slotTeacherId,
        studio_id: slot.studio_id, selected_date: date,
        start_time: slot.start_time, end_time: slot.end_time,
      });
      setHold(r.data);
      toast.success(`Slot held: ${slot.start_time} on ${date}${slot.teacher_name ? ` with ${slot.teacher_name}` : ""}`);
    } catch (e) {
      toast.error(errMsg(e));
      loadSlots(date);
    }
  };

  const submit = async () => {
    if (!form.parent_name || !form.parent_phone || !form.student_name) {
      toast.error("Parent name, phone and student name are required");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/public/enrolments/checkout", {
        session_id: session.session_id, hold_id: hold.id,
        parent_name: form.parent_name, parent_phone: form.parent_phone,
        parent_email: form.parent_email || null, student_name: form.student_name,
        student_age: form.student_age || null, notes: form.notes || null,
        preferred_day: form.preferred_day || null,
        rules_acknowledged: true, rules_version: rules?.version || "2026.1",
        origin_url: window.location.origin,
      });
      // Redirect to Stripe-hosted checkout — completion fires the post-payment
      // notifications + schedule + invoice on return.
      window.location.href = r.data.url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  const [pendingMaster, setPendingMaster] = useState(null);   // master tile being expanded for level pick

  const chooseCourse = async (c) => {
    setCourse(c);
    setTeacher(null);
    setStep(1); // Now jumps STRAIGHT to Date & time (skipping the old Instructor step)
    setPendingMaster(null);
    setCourseTeachers(null);
    try {
      const r = await api.get(`/public/course-teachers?course_id=${c.id}`);
      setCourseTeachers(r.data.teachers);
    } catch {
      setCourseTeachers([]);
    }
  };

  // Pre-select course/teacher from URL query (?courseId=...&teacherId=...).
  // Runs once when courses load.
  useEffect(() => {
    if (didPreselect) return;
    if (!session) return;
    if (!preselectCourseId && !preselectTeacherId) return;
    if (!courses) return;
    const c = preselectCourseId
      ? (courses || []).find((x) => x.id === preselectCourseId
          || (x.levels || []).some((l) => l.id === preselectCourseId))
      : null;
    if (preselectCourseId && c) {
      setDidPreselect(true);
      // If the matched id is actually a level within a master, swap to that level course
      const isLevelMatch = c.id !== preselectCourseId
        && (c.levels || []).some((l) => l.id === preselectCourseId);
      const target = isLevelMatch
        ? { ...c, id: preselectCourseId, is_master: false }
        : c;
      (async () => {
        await chooseCourse(target);
        if (preselectTeacherId) {
          try {
            const r = await api.get(`/public/course-teachers?course_id=${target.id}`);
            const t = (r.data.teachers || []).find((x) => x.id === preselectTeacherId);
            if (t) { setTeacher(t); setStep(1); }  // jump straight to Date & time
          } catch { /* fall through */ }
        }
      })();
    } else if (preselectTeacherId && !preselectCourseId) {
      // Teacher-only deep link: stay on Course step but mark didPreselect so we
      // don't re-run; pre-select happens after parent picks a course.
      setDidPreselect(true);
      // Keep teacherId in a holding cell so we can auto-pick after course choice.
      setPendingTeacherFromUrl(preselectTeacherId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses, session, preselectCourseId, preselectTeacherId, didPreselect]);

  const [pendingTeacherFromUrl, setPendingTeacherFromUrl] = useState(null);

  // When user picks a course AFTER a teacher-only deep link, auto-pick that teacher
  // if they're among the matched instructors for the chosen course.
  useEffect(() => {
    if (!pendingTeacherFromUrl || !courseTeachers) return;
    const t = (courseTeachers || []).find((x) => x.id === pendingTeacherFromUrl);
    if (t) {
      setTeacher(t);
      setStep(1);  // Date & time
      setPendingTeacherFromUrl(null);
    }
  }, [pendingTeacherFromUrl, courseTeachers]);
  const onTileClick = (tile) => {
    if (tile.is_master) {
      // Expand the master so the parent can pick a specific level
      setPendingMaster(pendingMaster?.master_course_name === tile.master_course_name ? null : tile);
    } else {
      // Stand-alone course: jump straight to teachers
      chooseCourse(tile);
    }
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const nextDateForDay = (dayIdx) => {
    const d = new Date();
    const jsTarget = (dayIdx + 1) % 7; // our 0=Mon -> JS 1=Mon
    let add = (jsTarget - d.getDay() + 7) % 7;
    if (add === 0) add = 7; // next week, not today (slots need lead time)
    d.setDate(d.getDate() + add);
    return d.toISOString().slice(0, 10);
  };
  const pickPreferredDay = (dayIdx) => {
    set("preferred_day", DAY_NAMES[dayIdx]);
    const d = nextDateForDay(dayIdx);
    setDate(d);
    releaseHold();
    loadSlots(d);
  };

  // From a teacher card's availability chip: select teacher + day in one tap
  const pickTeacherDay = (t, dayIdx) => {
    setTeacher(t);
    set("preferred_day", DAY_NAMES[dayIdx]);
    const d = nextDateForDay(dayIdx);
    setDate(d);
    releaseHold();
    setStep(1);
    loadSlots(d, t);
  };

  if (paymentReturn) {
    const p = paymentReturn;
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="enrolment-payment-return">
          {p.status === "polling" && (
            <>
              <Spinner />
              <h1 className="text-2xl font-bold mt-6 mb-2">Confirming your payment…</h1>
              <p className="text-muted-foreground">This usually takes a few seconds.</p>
            </>
          )}
          {p.status === "paid" && (
            <>
              <CheckCircle2 className="h-14 w-14 text-[#2D6A4F] mx-auto mb-6" strokeWidth={1.5} />
              <h1 className="text-3xl sm:text-4xl font-bold mb-3" data-testid="enrolment-paid-heading">Enrolment confirmed!</h1>
              <p className="text-muted-foreground">Invoice <span className="font-mono font-bold text-foreground">{p.invoice_number}</span></p>
              <div className="rounded-lg border bg-card p-6 mt-8 text-left text-sm space-y-2">
                <p><span className="text-muted-foreground">Student:</span> <b>{p.student_name}</b></p>
                <p><span className="text-muted-foreground">Course:</span> <b>{p.course_name}</b></p>
                <p><span className="text-muted-foreground">Music Instructor:</span> <b>{p.teacher_name}</b></p>
                <p><span className="text-muted-foreground">First lesson:</span> <b>{p.selected_date} · {p.selected_start_time}–{p.selected_end_time}</b></p>
                {p.studio_name && <p><span className="text-muted-foreground">Studio:</span> <b>{p.studio_name}</b></p>}
                <p className="pt-2 border-t"><span className="text-muted-foreground">Total paid:</span> <b className="font-display">{fmtRM(p.total_paid)}</b></p>
              </div>
              <p className="text-sm text-muted-foreground mt-6">A receipt and welcome message has been sent to your email and WhatsApp. Your Music Instructor has been notified.</p>
              <Button asChild className="mt-8" data-testid="enrolment-home-button"><Link to="/">Back to home</Link></Button>
            </>
          )}
          {(p.status === "expired" || p.status === "timeout") && (
            <>
              <h1 className="text-2xl font-bold mb-3">Payment not completed</h1>
              <p className="text-muted-foreground mb-6">Your slot has been released. Please pick a time again.</p>
              <Button asChild><Link to="/register">Try again</Link></Button>
            </>
          )}
        </div>
      </PublicLayout>
    );
  }

  if (result) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="enrolment-success">
          <CheckCircle2 className="h-14 w-14 text-[#2D6A4F] mx-auto mb-6" strokeWidth={1.5} />
          <h1 className="text-3xl sm:text-4xl font-bold mb-3">Enrolment submitted!</h1>
          <p className="text-muted-foreground">Reference <span className="font-mono font-bold text-foreground" data-testid="enrolment-reference">{result.reference}</span></p>
          <div className="rounded-lg border bg-card p-6 mt-8 text-left text-sm space-y-2">
            <p><span className="text-muted-foreground">Course:</span> <b>{result.course_name}</b></p>
            <p><span className="text-muted-foreground">First lesson:</span> <b>{result.selected_date} · {result.selected_start_time}–{result.selected_end_time}</b></p>
            <p><span className="text-muted-foreground">Registration fee:</span> <b>{fmtRM(result.registration_fee_before_sst)}</b></p>
            <p><span className="text-muted-foreground">First month deposit:</span> <b>{fmtRM(result.deposit_amount)}</b></p>
            <p className="pt-2 border-t"><span className="text-muted-foreground">Payable on confirmation:</span> <b className="font-display">{fmtRM(result.total_payable)}</b> <span className="text-xs text-muted-foreground">(before SST)</span></p>
          </div>
          <p className="text-sm text-muted-foreground mt-6">{result.message}</p>
          <div className="rounded-lg border bg-secondary/5 p-4 mt-6 text-left text-xs text-muted-foreground">
            <p className="font-semibold text-foreground mb-1">🎶 Welcome to Cornerstone Music for your musical journey</p>
            <p>Thank you for accepting our Rules &amp; Regulations. You can review them anytime at <Link to="/calendar" className="underline">our school calendar page</Link>.</p>
          </div>
          <Button asChild className="mt-8" data-testid="enrolment-home-button"><Link to="/">Back to home</Link></Button>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-14" data-testid="register-page">
        <h1 className="text-4xl sm:text-5xl font-bold mb-3">Register Full Course</h1>
        <p className="text-muted-foreground mb-10 max-w-xl">Pick a course and a day &amp; time that works for you — we&apos;ll match you with an available Music Instructor. Starting from <b className="text-foreground">Level 1 fee</b>; admin will confirm your final grade after class.</p>
        <StepBar step={step} />

        {step === 0 && (
          <div data-testid="step-course">
            {(preselectMaster || pendingMaster) && (
              <div className="mb-4 flex items-center gap-3 rounded-md border bg-secondary/5 px-4 py-2.5 text-sm" data-testid="master-filter-banner">
                <span className="text-muted-foreground">Showing:</span>
                <b>{preselectMaster || pendingMaster?.name}</b>
                {preselectMaster ? (
                  <Link to="/register" className="ml-auto text-xs text-secondary hover:underline" data-testid="clear-master-filter">← Show all courses</Link>
                ) : (
                  <button onClick={() => setPendingMaster(null)} className="ml-auto text-xs text-secondary hover:underline" data-testid="clear-master-filter">← Show all courses</button>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-in">
              {(courses || [])
                .filter((c) => {
                  // Deep-link filter from /courses
                  if (preselectMaster) {
                    return (c.name || "").trim().toLowerCase() === preselectMaster.trim().toLowerCase();
                  }
                  // Inline expansion: when a master tile is expanded, hide other tiles
                  if (pendingMaster) {
                    return c.master_course_name === pendingMaster.master_course_name
                        || c.id === pendingMaster.id;
                  }
                  return true;
                })
                .map((c) => {
                const masterAutoExpand = preselectMaster
                  && (c.name || "").trim().toLowerCase() === preselectMaster.trim().toLowerCase()
                  && c.is_master;
                const isExpanded = c.is_master && (
                  pendingMaster?.master_course_name === c.master_course_name
                  || masterAutoExpand
                );
                return (
                <div key={c.id} className={`rounded-lg border bg-card overflow-hidden transition-colors ${isExpanded ? "border-secondary md:col-span-2 lg:col-span-3" : "lift hover:border-secondary"} ${course?.id === c.id ? "border-secondary" : ""}`}>
                <button disabled={!session} onClick={() => onTileClick(c)}
                  data-testid={`choose-course-${c.id}`}
                  className="text-left w-full disabled:opacity-50 disabled:cursor-wait">
                  {c.image_url
                    ? <img src={mediaUrl(c.image_url)} alt={c.image_alt_text || c.name} loading="lazy" className="w-full h-36 object-cover" />
                    : <div className="w-full h-36 bg-accent flex items-center justify-center"><Music4 className="h-8 w-8 text-secondary" strokeWidth={1.5} /></div>}
                  <div className="p-5">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold">{c.name}</p>
                      {c.is_master && <span className="text-[10px] uppercase font-bold tracking-wide text-secondary bg-secondary/10 px-2 py-0.5 rounded">{c.level_count} levels</span>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{c.description}</p>
                    {(c.age_group || c.lesson_duration) && (
                      <p className="text-[11px] text-muted-foreground mt-2">
                        {c.age_group && <>Age {c.age_group}</>}{c.age_group && c.lesson_duration && " · "}{c.lesson_duration}
                      </p>
                    )}
                    {c.fee_from && (
                      <p className="text-sm font-bold font-display mt-2">
                        {c.is_master ? "From " : ""}{fmtRM(c.fee_from)}/mo <span className="text-[10px] font-normal text-muted-foreground">inclusive of SST</span>
                      </p>
                    )}
                    {c.is_master && !isExpanded && (
                      <p className="text-xs text-secondary font-semibold mt-2">Tap to pick your level →</p>
                    )}
                  </div>
                </button>
                {isExpanded && (
                  <div className="border-t bg-secondary/5 p-5" data-testid={`master-levels-${c.id}`}>
                    <p className="text-xs text-muted-foreground mb-3">Pick the level you're enrolling for:</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                      {c.levels.map((lv) => (
                        <button key={lv.id} onClick={() => chooseCourse(lv)}
                          data-testid={`choose-level-${lv.id}`}
                          className="rounded-md border bg-card hover:border-secondary p-3 text-left transition">
                          <p className="text-sm font-semibold">{lv.name.replace(c.master_course_name, "").trim() || `Level ${lv.level_within_master}`}</p>
                          <p className="text-xs text-muted-foreground">{lv.fee_from ? `${fmtRM(lv.fee_from)}/mo` : "—"}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                </div>
              );})}
            </div>
          </div>
        )}

        {step === 1 && (
          <div data-testid="step-slot">
            <p className="text-sm text-muted-foreground mb-4">
              Pick a day &amp; time that works for you — we&apos;ll match you with an available Music Instructor for <b className="text-foreground">{course?.name}</b>{teacher ? <> ({teacher.full_name})</> : null}.
            </p>
            <div className="mb-5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Preferred day for weekly lessons</Label>
              <div className="flex flex-wrap gap-2 mt-2" data-testid="preferred-day-chips">
                {DAY_NAMES.map((d, i) => (
                  <button key={d} onClick={() => pickPreferredDay(i)}
                    data-testid={`preferred-day-${i}`}
                    className={`px-3.5 py-2 rounded-full border text-xs font-semibold transition-colors
                      ${form.preferred_day === d ? "bg-[#2D6A4F] text-white border-[#2D6A4F]" : "bg-card hover:border-secondary"}`}>
                    {d.slice(0, 3)}
                  </button>
                ))}
              </div>
              {form.preferred_day && (
                <p className="text-xs text-muted-foreground mt-2">
                  Weekly lessons preferred on <b>{form.preferred_day}s</b> — we've jumped to the next {form.preferred_day} for you, or pick any other date below.
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-end gap-4 mb-6">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Preferred date</Label>
                <Input type="date" min={todayStr()} value={date} data-testid="slot-date-input"
                  onChange={(e) => { setDate(e.target.value); releaseHold(); if (e.target.value) loadSlots(e.target.value); }}
                  className="w-48" />
              </div>
              {hold && <HoldCountdown expiresAt={hold.expires_at} onExpired={() => { setHold(null); toast.error("Slot hold expired — please pick a time again"); if (date) loadSlots(date); }} />}
            </div>
            {loadingSlots ? <Spinner /> : slots == null ? (
              <p className="text-sm text-muted-foreground">
                Choose a date to see free times for <b>{course?.name}</b>
                {teacher ? <> with <b>{teacher.full_name}</b></> : null}.
              </p>
            ) : holidayInfo ? (
              <div className="rounded-lg border-2 border-dashed border-secondary/50 bg-secondary/5 p-6 text-center" data-testid="holiday-banner">
                <CalendarDays className="h-7 w-7 text-secondary mx-auto mb-2" strokeWidth={1.5} />
                <p className="font-semibold text-foreground">{holidayInfo.label}</p>
                <p className="text-xs text-muted-foreground mt-1">School closed on this date — please choose another day.</p>
              </div>
            ) : slots.length === 0 ? (
              <EmptyState text="No free slots on this date — try another day." />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 stagger-in" data-testid="slot-grid">
                {slots.map((s) => {
                  const isHeld = hold && hold.start_time === s.start_time && hold.end_time === s.end_time;
                  const slotTeacher = s.teacher_name || teacher?.full_name;
                  return (
                    <button key={`${s.start_time}_${s.teacher_id || ''}`} onClick={() => pickSlot(s)}
                      data-testid={`slot-${s.start_time.replace(":", "")}`}
                      className={`flex flex-col items-start gap-1 px-3 py-2.5 rounded-md border text-left transition-colors
                        ${isHeld ? "bg-secondary text-white border-secondary" : "bg-card hover:border-secondary"}`}>
                      <span className="text-sm font-semibold font-mono">{s.start_time}–{s.end_time}</span>
                      {slotTeacher && (
                        <span className={`text-[10px] truncate w-full ${isHeld ? "text-white/90" : "text-muted-foreground"}`}>
                          with {slotTeacher}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            {hold && (
              <p className="text-xs text-muted-foreground mt-4">
                Held: {date} · {hold.start_time}–{hold.end_time}
              </p>
            )}
            <div className="mt-8 pt-6 border-t flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="preferred-time-cta-wrap">
              <div className="text-xs text-muted-foreground max-w-md">
                Can&apos;t find a slot that works? Tell us your preferred day &amp; time ranges and we&apos;ll get back to you with the closest match.
              </div>
              <Button variant="outline" onClick={() => setPrefDialogOpen(true)} data-testid="preferred-time-cta">
                <CalendarSearch className="h-4 w-4 mr-1.5" />Submit preferred times
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="max-w-lg space-y-4" data-testid="step-details">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Parent name *</Label>
                <Input data-testid="reg-parent-name" value={form.parent_name || ""} onChange={(e) => set("parent_name", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Phone *</Label>
                <Input data-testid="reg-parent-phone" value={form.parent_phone || ""} onChange={(e) => set("parent_phone", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Email</Label>
                <Input data-testid="reg-parent-email" type="email" value={form.parent_email || ""} onChange={(e) => set("parent_email", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Student name *</Label>
                <Input data-testid="reg-student-name" value={form.student_name || ""} onChange={(e) => set("student_name", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Student age</Label>
                <Input data-testid="reg-student-age" value={form.student_age || ""} onChange={(e) => set("student_age", e.target.value)} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Anything we should know?</Label>
              <Textarea data-testid="reg-notes" rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} />
            </div>
            <div className="rounded-lg border-2 border-secondary/30 bg-secondary/5 p-5 mt-2" data-testid="rules-box">
              <p className="text-xs font-bold uppercase tracking-wide text-secondary mb-2">School Rules & Regulations</p>
              <div className="max-h-56 overflow-y-auto pr-2 text-sm leading-relaxed whitespace-pre-line text-foreground/85"
                   data-testid="rules-text">
                {rules?.rules || "Loading rules…"}
              </div>
              <label className="flex items-start gap-2 mt-4 cursor-pointer" data-testid="rules-checkbox-label">
                <input type="checkbox" className="mt-1 h-4 w-4 accent-[#2D6A4F]"
                  checked={rulesAccepted} onChange={(e) => setRulesAccepted(e.target.checked)}
                  data-testid="rules-checkbox" />
                <span className="text-sm">
                  I have read and agree to Cornerstone Music School's Rules & Regulations.
                </span>
              </label>
            </div>
          </div>
        )}

        {step === 3 && hold && (
          <div className="max-w-lg" data-testid="step-review">
            <div className="rounded-lg border bg-card p-6 text-sm space-y-3">
              <p className="flex items-center gap-2"><Music4 className="h-4 w-4 text-secondary" /><b>{course?.name}</b>{teacher?.full_name ? <> with <b>{teacher.full_name}</b></> : null}</p>
              <p className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-secondary" />{date} ({dayName(new Date(date + "T00:00:00").getDay() === 0 ? 6 : new Date(date + "T00:00:00").getDay() - 1)}) · {hold.start_time}–{hold.end_time}</p>
              <p className="flex items-center gap-2"><User className="h-4 w-4 text-secondary" />{form.student_name} ({form.student_age || "age not given"}) · parent {form.parent_name}, {form.parent_phone}</p>
            </div>

            {quote && (
              <div className="mt-4 rounded-lg border bg-secondary/5 p-5 text-sm space-y-1.5" data-testid="enrolment-quote">
                <h3 className="font-semibold text-base mb-3">Payable today</h3>
                <div className="rounded-md border border-secondary/30 bg-card/60 px-3 py-2 text-[11px] text-muted-foreground mb-3" data-testid="level-1-disclaimer">
                  Starting from <b className="text-foreground">Level 1 fee</b>. Final fee may be adjusted by admin after course level/grade confirmation.
                </div>
                {quote.lines.map((ln, i) => (
                  <div key={i} className="flex justify-between" data-testid={`fee-line-${ln.kind}-${i}`}>
                    <span className={ln.kind === "liability" ? "text-amber-700" : ln.kind === "tax" ? "text-muted-foreground" : "text-muted-foreground"}>
                      {ln.label}
                    </span>
                    <b className={ln.kind === "liability" ? "text-amber-700" : ""}>{fmtRM(ln.amount)}</b>
                  </div>
                ))}
                <div className="flex justify-between pt-3 mt-1 border-t text-base">
                  <span className="font-semibold">Total payable now</span>
                  <b className="font-display text-secondary text-lg" data-testid="enrolment-total">{fmtRM(quote.total_payable)}</b>
                </div>
                <p className="text-[11px] text-muted-foreground pt-2 italic">
                  <b className="text-amber-700">Deposit is non-refundable</b> — it will be applied to your final month's fees if you give 30 days drop-out notice.
                  After payment, your slot is instantly confirmed and your parent login + e-invoice will be emailed.
                </p>
              </div>
            )}
            <div className="mt-4"><HoldCountdown expiresAt={hold.expires_at} onExpired={() => { setHold(null); setStep(1); toast.error("Slot hold expired — please pick a time again"); if (date) loadSlots(date); }} /></div>
          </div>
        )}

        <div className="flex items-center gap-3 mt-10">
          {step > 0 && (
            <Button variant="outline" data-testid="wizard-back"
              onClick={() => setStep(step - 1)}>
              <ArrowLeft className="h-4 w-4 mr-1" />Back
            </Button>
          )}
          {step === 1 && (
            <Button disabled={!hold} onClick={() => setStep(2)} data-testid="wizard-next-slot">
              Continue <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          )}
          {step === 2 && (
            <Button disabled={!form.parent_name || !form.parent_phone || !form.student_name || !rulesAccepted}
              onClick={() => { if (!hold) { toast.error("Your slot hold expired — pick a time again"); setStep(1); return; } setStep(3); }}
              data-testid="wizard-next-details">
              Review <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          )}
          {step === 3 && (
            <Button size="lg" className="bg-secondary hover:bg-[#D96C52]" disabled={busy || !hold || !session}
              onClick={submit} data-testid="wizard-submit">
              {busy ? "Redirecting to payment..." : "Pay & confirm enrolment"}
            </Button>
          )}
        </div>
      </div>
      <PreferredTimeDialog open={prefDialogOpen} onOpenChange={setPrefDialogOpen} course={course} />
    </PublicLayout>
  );
}
